import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import {
  Search, Video, Clock, ExternalLink, AlertTriangle,
  CheckCircle2, Loader2, Play, Award, User, FileDown, Share2
} from 'lucide-react';
import { getInterviewHistory, exportInterviewReport, createShareLink } from '../services/api';
import { toast } from 'sonner';

const statusConfig = {
  completed: { color: 'bg-green-500/10 text-green-500 border-green-500/20', icon: CheckCircle2, label: 'Completed' },
  in_progress: { color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', icon: Play, label: 'In Progress' },
  pending: { color: 'bg-muted text-muted-foreground', icon: Clock, label: 'Pending' }
};

const InterviewHistoryRow = ({ session }) => {
  const cfg = statusConfig[session.status] || statusConfig.pending;
  const Icon = cfg.icon;
  const scores = session.summary_scores || {};

  const handleViewResults = () => {
    window.open(`/interview/${session.token}?recruiter=1`, '_blank');
  };

  const handleDownloadPdf = async () => {
    try {
      const blob = await exportInterviewReport(session.job_id, session.candidate_id, 'pdf');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Interview_Report_${(session.candidate_name || 'Candidate').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF report downloaded!');
    } catch (error) {
      toast.error(error?.response?.status === 404 ? 'No completed interview found' : 'Failed to download report');
    }
  };

  const handleShare = async () => {
    try {
      const result = await createShareLink(session.token, 7);
      const shareUrl = `${(process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || window.location.origin}/shared/${result.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Shareable link copied! Valid for 7 days.', { duration: 4000 });
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 border rounded-lg hover:border-primary/30 transition-colors group"
      data-testid={`interview-history-${session.id}`}>
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
        <span className="text-sm font-bold text-primary">
          {(session.candidate_name || '?')[0].toUpperCase()}
        </span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-medium text-sm truncate">
            {session.candidate_name || 'Unknown Candidate'}
          </span>
          <Badge className={`${cfg.color} text-[10px] px-1.5 py-0 gap-0.5`}>
            <Icon className="h-3 w-3" />
            {cfg.label}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground truncate">{session.job_title}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          {session.candidate_email && <span>{session.candidate_email}</span>}
          {session.attempt_number > 1 && <span>Attempt #{session.attempt_number}</span>}
          {session.proctoring_violations > 0 && (
            <span className="flex items-center gap-0.5 text-destructive">
              <AlertTriangle className="h-3 w-3" />
              {session.proctoring_violations} violation{session.proctoring_violations > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Score (if completed) */}
      {session.status === 'completed' && scores.overall ? (
        <div className="text-center shrink-0">
          <div className={`text-xl font-bold ${
            scores.overall >= 80 ? 'text-green-500' :
            scores.overall >= 65 ? 'text-blue-500' :
            scores.overall >= 50 ? 'text-yellow-500' : 'text-red-500'
          }`}>
            {Math.round(scores.overall)}
          </div>
          <p className="text-[10px] text-muted-foreground">Score</p>
        </div>
      ) : null}

      {/* Recommendation */}
      {scores.recommendation && (
        <Badge variant="outline" className="hidden sm:flex text-xs shrink-0">
          <Award className="h-3 w-3 mr-1" />
          {scores.recommendation}
        </Badge>
      )}

      {/* Date */}
      <div className="text-right shrink-0 hidden md:block">
        <p className="text-xs text-muted-foreground">
          {new Date(session.created_at).toLocaleDateString()}
        </p>
        <p className="text-[10px] text-muted-foreground">
          {new Date(session.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>

      {/* Actions */}
      {session.status === 'completed' && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            className="shrink-0"
            data-testid={`share-interview-${session.id}`}
          >
            <Share2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownloadPdf}
            className="shrink-0"
            data-testid={`download-interview-pdf-${session.id}`}
          >
            <FileDown className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={handleViewResults}
        className="shrink-0"
        data-testid={`view-interview-${session.id}`}
      >
        <ExternalLink className="h-3.5 w-3.5 mr-1" />
        View
      </Button>
    </div>
  );
};

export const InterviewHistory = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const fetchHistory = useCallback(async (reset = true) => {
    try {
      setLoading(true);
      const skip = reset ? 0 : sessions.length;
      const data = await getInterviewHistory(search, statusFilter, 50, skip);
      if (reset) {
        setSessions(data.sessions || []);
      } else {
        setSessions(prev => [...prev, ...(data.sessions || [])]);
      }
      setTotal(data.total || 0);
      setHasMore(data.has_more || false);
    } catch (err) {
      console.error('Failed to fetch interview history:', err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => fetchHistory(true), 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter]);

  const completedCount = sessions.filter(s => s.status === 'completed').length;
  const inProgressCount = sessions.filter(s => s.status === 'in_progress').length;
  const pendingCount = sessions.filter(s => s.status === 'pending').length;

  return (
    <div className="space-y-4" data-testid="interview-history-panel">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 border rounded-lg text-center">
          <p className="text-2xl font-bold text-green-500">{completedCount}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </div>
        <div className="p-3 border rounded-lg text-center">
          <p className="text-2xl font-bold text-yellow-500">{inProgressCount}</p>
          <p className="text-xs text-muted-foreground">In Progress</p>
        </div>
        <div className="p-3 border rounded-lg text-center">
          <p className="text-2xl font-bold text-muted-foreground">{pendingCount}</p>
          <p className="text-xs text-muted-foreground">Pending</p>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by candidate name or email..."
            className="pl-9 h-9"
            data-testid="interview-history-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] h-9" data-testid="interview-history-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        {total > 0 && (
          <Badge variant="secondary" className="text-xs shrink-0">{total} total</Badge>
        )}
      </div>

      {/* List */}
      {loading && sessions.length === 0 ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Video className="h-12 w-12 mx-auto mb-4 opacity-50" strokeWidth={1.5} />
          <p>{search ? 'No interviews match your search' : 'No interviews yet'}</p>
          <p className="text-sm mt-1">Generate interview links from the AI Interview tab to get started</p>
        </div>
      ) : (
        <ScrollArea className="max-h-[500px]">
          <div className="space-y-2">
            {sessions.map(s => (
              <InterviewHistoryRow key={s.id} session={s} />
            ))}
          </div>
          {hasMore && (
            <div className="text-center pt-3">
              <Button variant="outline" size="sm" onClick={() => fetchHistory(false)} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Load More
              </Button>
            </div>
          )}
        </ScrollArea>
      )}
    </div>
  );
};

export default InterviewHistory;
